import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";

import { User } from "../../../../../types/types";
import { useProfileData } from "../../../../../hooks/useProfileData";

import { useUserDataFormatter } from "../../../../User/hooks/useUserDataFormatter";

import Message from "../../../components/Message";
import { Message as MessageType } from "../../../types";
import DateDivider from "../../../components/DateDivider";
import { useMessageGroups } from "../../../hooks/useMessageGroups";

const MessagesList = forwardRef<
  HTMLDivElement,
  {
    senderUserData?: User;
    isSenderDataLoading: boolean;
    messages: MessageType[];
    isMessagesLoading: boolean;
  }
>(({ senderUserData, isSenderDataLoading, messages, isMessagesLoading }, ref) => {
  const { i18n } = useTranslation();

  const messageGroups = useMessageGroups(messages, i18n.language);

  const { currentUser, otherUser, isUsersInfoLoading } = useMessageUsersFormatters(senderUserData, isSenderDataLoading);

  const IS_MESSAGES_LOADING = isMessagesLoading || isUsersInfoLoading;

  if (IS_MESSAGES_LOADING) return <MessagesListLoader />;
  if (messages.length === 0) return <EmptyMessageList />;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.messagesContainer} ref={ref}>
        {messageGroups.map(({ date, messages }, index) => (
          <Box key={date} sx={styles.group}>
            {index !== 0 && <DateDivider date={date} />}
            <Box sx={styles.messages}>
              {messages.map((message) => (
                <Message
                  key={message.uuid}
                  message={message}
                  currentUser={currentUser}
                  otherUser={otherUser}
                  isReversed={false}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default MessagesList;

const useMessageUsersFormatters = (senderUserData?: User, isSenderDataLoading?: boolean) => {
  const {
    id: otherUserId,
    fullName: otherUserFullName,
    firstTwoChr: otherUserFirstTwoChr,
    media: otherUserMedia,
  } = useUserDataFormatter(senderUserData);

  const { data: currentUserData, isLoading: isCurrentUserDataLoading } = useProfileData();
  const {
    id: currentUserId,
    fullName: currentUserFullName,
    firstTwoChr: currentUserFirstTwoChr,
    media: currentUserMedia,
  } = currentUserData;
  const currentUser = {
    id: currentUserId!,
    fullName: currentUserFullName,
    firstTwoChr: currentUserFirstTwoChr,
    media: currentUserMedia,
  };

  const otherUser = {
    id: otherUserId!,
    fullName: otherUserFullName,
    firstTwoChr: otherUserFirstTwoChr,
    media: otherUserMedia,
  };

  return { currentUser, otherUser, isUsersInfoLoading: isCurrentUserDataLoading || isSenderDataLoading };
};

const EmptyMessageList = () => {
  const { t } = useTranslation("chat");
  return (
    <Box sx={styles.container}>
      <Typography sx={styles.emptyMessageListText}>{t("noMessages")}</Typography>
    </Box>
  );
};

const MessagesListLoader = () => {
  return (
    <Box sx={styles.container}>
      <Box sx={{ ...styles.messagesContainer, justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={200} />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    flex: 1,
    padding: "12px 12px 12px 0",
    borderBottom: "1px solid #EAECF0",
    marginBottom: "16px",
  },
  messagesContainer: {
    height: "100%",
    maxHeight: "620px",
    padding: "0 12px 0 24px",
    display: "flex",
    flexDirection: "column",
    rowGap: "30px",
    overflowY: "auto",
  },
  emptyMessageListText: {
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "normal",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  messages: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
};
